import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, DialogOverlay, DialogOverlayActions, Paragraph, TextField } from '@fs/zion-ui'
import useNotifications from '../../../hooks/useNotifications'
import { SCREENING_STATE } from '../util/artifactsConstants'
import { updateArtifact } from '../../../services/artifactService'
import RestrictArtifactsDialogOptions from './RestrictArtifactsDialogOptions'

export default function RestrictSelectedArtifactsOverlay({
  checkedArtifacts,
  setCheckedArtifacts,
  onSuccess,
  patronId,
  ...overlay
}) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const [pendingScreeningTag, setPendingScreeningTag] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [idConfirmation, setIdConfirmation] = useState('')
  const focusElementRef = useRef()

  const restrictSelectedOnSubmit = async () => {
    overlay.close()
    const restrictArtifactResponses = await Promise.allSettled(
      checkedArtifacts.map((artifactId) => {
        return updateArtifact(
          {
            id: artifactId,
            ID: artifactId,
            screeningState: SCREENING_STATE.RESTRICTED,
            screeningTag: pendingScreeningTag,
          },
          sendEmail
        )
      })
    )
    const failedToRestrictArtifactIds = []
    restrictArtifactResponses.forEach((response) => {
      if (response.status === 'rejected') {
        const payload = JSON.parse(response.reason.config.data)
        failedToRestrictArtifactIds.push(payload.id)
      }
    })
    if (restrictArtifactResponses.length === failedToRestrictArtifactIds.length) {
      error(t('artifacts.restrictSelectedAllFailed', 'Error restricting selected artifacts'))
    } else if (failedToRestrictArtifactIds.length > 0) {
      error(
        t(
          'artifacts.restrictSelectedSomeFailed',
          'Error restricting the following artifact IDs: { failedToRestrictArtifactIds }',
          {
            failedToRestrictArtifactIds: JSON.stringify(failedToRestrictArtifactIds),
          }
        )
      )
    } else {
      ok(t('artifacts.restrictSelectedSuccess', 'Restricted selected artifacts'))
      onSuccess()
    }
    setCheckedArtifacts((prev) => prev.filter((artifactId) => failedToRestrictArtifactIds.includes(artifactId)))
  }

  return (
    <DialogOverlay focusRef={focusElementRef} title={t('action.restrictSelected', 'Restrict Selected')} {...overlay}>
      <Paragraph size="sm">
        {checkedArtifacts && checkedArtifacts.length > 1
          ? t('artifacts.restrictMulipleConfirmation', 'Are you sure you want to restrict these artifacts?')
          : t('artifacts.restrictConfirmation', 'Are you sure you want to restrict this artifact?')}
      </Paragraph>
      <RestrictArtifactsDialogOptions
        pendingScreeningTag={pendingScreeningTag}
        setPendingScreeningTag={setPendingScreeningTag}
      />
      <Paragraph size="sm">
        {`${t('artifacts.restrictAllEnterPatronId', 'Please type in this patron’s ID to confirm.')} (${patronId})`}
      </Paragraph>
      {patronId && (
        <TextField
          ref={focusElementRef}
          id="restrictSelectedPatronIdInput"
          label={t('artifacts.patronId', 'Patron ID')}
          value={idConfirmation}
          onChange={(e) => setIdConfirmation(e.target.value)}
          type="text"
          fullwidth="true"
        />
      )}
      <Checkbox
        name="NotifyPatronViaEmailCheckbox"
        label={t('artifacts.restrictAllNotifyPatron', 'Notify patron via email')}
        checked={sendEmail}
        onChange={(e) => setSendEmail(e.target.checked)}
      />
      <DialogOverlayActions>
        <Button
          onClick={restrictSelectedOnSubmit}
          emphasis="high"
          disabled={!(pendingScreeningTag && (!patronId || Number(idConfirmation) === patronId))}
        >
          {t('action.submit', 'Submit')}
        </Button>
        <Button onClick={overlay.close}>{t('action.cancel', 'Cancel')}</Button>
      </DialogOverlayActions>
    </DialogOverlay>
  )
}
